import React, { useEffect, useState } from 'react';
import { sendSearchAnalyticsEvent } from '@mb/lib';
import { getUserLang, isEuEnv } from '@mb/lib';
import ClickAwayListener from '@mui/base/ClickAwayListener';
import { AutocompleteItemType } from 'components/components/SearchAutocompleteResults/SearchAutocompleteResults.types';
import debounce from 'lodash/debounce';
import ReactDOM from 'react-dom';

import * as Styled from './AutocompleteResultsContainer.styles';
import { SuggestedItemsRenderer } from './SuggestedItemsRenderer';
import { useAutocompleteEventListeners } from './useAutocompleteEventListeners';
import { deleteHistoryItem } from '../../api/autocomplete';
import {
  getIsBrands,
  getShouldShowHistory,
  getShouldShowImages,
  shouldRedirectToSearch,
} from '../../helpers';
import {
  getQuerySelector,
  getIsOpenSelector,
  getHistorySelector,
  getIsLoadingSelector,
  getSuggestedItemsSelector,
  currentItemIndexSelector,
  currentProductIndexSelector,
} from '../../redux/selectors/autocompleteSelector';
import {
  setIsOpen,
  setQuery,
} from '../../redux/sliceCreators/autocompleteSlice';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { RecommendationItem } from '../../types';

interface AutocompleteResultsContainerProps {
  searchContainerId: string;
  autocompleteContainerId: string;
  absoluteContainer?: React.ReactElement<{
    open?: boolean;
    topOffset?: number;
  }>;
  autocompleteWrapper?: React.ReactElement<{ searchFormRect?: DOMRect }>;
  backdrop?: React.ReactElement<{ open?: boolean }>;
  contentPadding?: string;
  borderRadius?: string;
  boxShadow?: string;
  onClose?: () => void;
}

const AutocompleteResultsContainer: React.FC<
  AutocompleteResultsContainerProps
> = ({
  searchContainerId,
  autocompleteContainerId,
  absoluteContainer,
  autocompleteWrapper,
  backdrop,
  onClose,
}) => {
  const query = useAppSelector(getQuerySelector);
  const suggestedItems = useAppSelector(getSuggestedItemsSelector);
  const searchHistory = useAppSelector(getHistorySelector);
  const isOpen = useAppSelector(getIsOpenSelector);
  const isLoading = useAppSelector(getIsLoadingSelector);
  const currentIndex = useAppSelector(currentItemIndexSelector);
  const currentProductIndex = useAppSelector(currentProductIndexSelector);
  const dispatch = useAppDispatch();
  const {
    brands,
    shouldShowSuggestedItems,
    correctionQuery,
    resultItems,
    results,
    shouldShowSuggestions,
  } = useAutocompleteEventListeners();

  const shouldShowHistory = getShouldShowHistory(query, searchHistory);
  const shouldShowImages = getShouldShowImages(query, isLoading);
  const suggestedItemsToShow: RecommendationItem[] = suggestedItems;
  const shouldOpen = isOpen;

  // Cache DOM elements and their rects to avoid repeated queries and layout calculations
  const [domElements, setDomElements] = useState<{
    autocompleteContainer: HTMLElement | null;
    headerSearchForm: HTMLElement | null;
    autocompleteRect: DOMRect | null;
    headerRect: DOMRect | null;
  }>({
    autocompleteContainer: null,
    headerSearchForm: null,
    autocompleteRect: null,
    headerRect: null,
  });

  // Update DOM elements when IDs change or on mount
  useEffect(() => {
    const updateDomElements = () => {
      const autocompleteContainer = document.getElementById(
        autocompleteContainerId,
      );
      const headerSearchForm = document.getElementById(searchContainerId);

      setDomElements({
        autocompleteContainer,
        headerSearchForm,
        autocompleteRect:
          autocompleteContainer?.getBoundingClientRect() || null,
        headerRect: headerSearchForm?.getBoundingClientRect() || null,
      });
    };

    // Initial setup
    updateDomElements();

    // Update on window resize
    const handleResize = debounce(updateDomElements, 100);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [autocompleteContainerId, searchContainerId]);

  const handleClickAway = (event: any) => {
    if (!isOpen) {
      return;
    }

    const isSearchFormClicked = event.target.closest(
      searchContainerId ? `#${searchContainerId}` : '#headerSearchForm',
    );
    const isQuickViewClicked = event.target.closest('#QuickViewModal');
    const isMiniPdpClicked = event.target.closest('#miniPdpV2Modal');

    if (!(isSearchFormClicked || isQuickViewClicked || isMiniPdpClicked)) {
      dispatch(setIsOpen(false));
      onClose?.();
      window.dispatchEvent(
        new CustomEvent('toggle-main-search', {
          detail: { show: false },
        }),
      );
    }
  };

  const handleClickAwayAutocompleteResults = (event: any) => {
    const isAutocompleteResultsClicked = event.target.closest(
      '#searchAutocompleteResults',
    );
    const isSuggestedItemsClicked = event.target.closest(
      '#searchSuggestedItemsWrapper',
    );

    if (!(isAutocompleteResultsClicked || isSuggestedItemsClicked)) {
      dispatch(setIsOpen(false));
      onClose?.();
      window.dispatchEvent(
        new CustomEvent('toggle-main-search', {
          detail: { show: false },
        }),
      );
    }
  };

  const handleItemClick = (value: string, type: AutocompleteItemType) => {
    sessionStorage.setItem('ssScroll', '0');
    sessionStorage.setItem('prevSearchUrl', 'null');
    let suggesterType: undefined | string = undefined;
    let suggesterValue = value;
    let query = value;
    let historyQuery = null;

    window.scrollTo(0, 0);

    if (type === AutocompleteItemType.HISTORY) {
      const historyItem = searchHistory.find(
        (item: any) => item.value === value,
      );
      if (historyItem?.suggesterType != null) {
        suggesterType = historyItem?.suggesterType;
      }
      if (historyItem?.suggesterValue != null) {
        suggesterValue = historyItem?.suggesterValue;
      }
      query = historyItem?.value || '';
      historyQuery = historyItem?.value;
      dispatch(setQuery(historyQuery || ''));
    }

    handleSearch({
      query,
      suggesterType,
      suggesterValue,
      historyQuery,
      searchType: type,
    });
  };

  const handleSearch = (args: any) => {
    const suggesterType = args.suggesterType
      ? `&suggesterType=${args.suggesterType}`
      : '';
    let suggesterValue = args.suggesterValue
      ? `&suggesterValue=${args.suggesterValue}`
      : '';
    const searchType = args.searchType ? `&searchType=${args.searchType}` : '';
    let newQuery = args?.historyQuery || query;
    // Fix for Defaults suggestions, which don't have suggesterType/suggesterValue
    const isDefaultSuggestion =
      !Boolean(args.suggesterType) && Boolean(args?.suggesterValue);

    const storageLang = getUserLang();
    const searchUrlPrefix = !isEuEnv
      ? ''
      : storageLang != null
      ? `/${storageLang}`
      : '';

    if (shouldRedirectToSearch()) {
      if (isDefaultSuggestion) {
        suggesterValue = '';
        newQuery = args.suggesterValue;
      }
      window.location.assign(
        `${searchUrlPrefix}/search?q=${newQuery}${suggesterType}${suggesterValue}${searchType}`,
      );
      sendSearchAnalyticsEvent({
        query: newQuery,
        searchType: args.searchType,
      });
      return;
    }

    const stringId = String(args.query);
    if (getIsBrands()) {
      const brand = brands.find((brand) => brand.id === stringId);
      if (brand == null) {
        return;
      }
      const url = `${brand?.search_for_button}#/filterValueLimiter=true`;
      window.location.assign(url);
      return;
    } else {
      if (isDefaultSuggestion) {
        dispatch(setQuery(args.suggesterValue));
      }

      const query = isDefaultSuggestion ? args.suggesterValue : newQuery;
      const suggesterValue = isDefaultSuggestion ? null : args.suggesterValue;
      const searchFormSubmit = new CustomEvent('searchFormSubmit', {
        detail: {
          query,
          page: 1,
          action: 'searchBar',
          suggesterType: args.suggesterType,
          suggesterValue,
          searchType: args.searchType,
          additionalFilters: {},
        },
      });
      document.dispatchEvent(searchFormSubmit);
      sendSearchAnalyticsEvent({
        query: query,
        searchType: args.searchType,
      });
    }
    dispatch(setIsOpen(false));
    onClose?.();
  };

  const handleQueryClick = (newValue: string) => {
    if (query !== newValue) {
      dispatch(setQuery(newValue));
      handleSearch({
        query: newValue,
        suggesterType: 'didyoumean',
        suggesterValue: correctionQuery?.value,
        historyQuery: null,
      });
    } else {
      handleSearch({
        query: newValue,
        historyQuery: null,
      });
    }
  };

  const handleClearHistoryItem = (value: string) => {
    dispatch(deleteHistoryItem(value));
  };

  const AbsoluteContainer = React.isValidElement(absoluteContainer)
    ? () => absoluteContainer
    : absoluteContainer || Styled.AbsoluteContainer;
  const AutocompleteWrapper = React.isValidElement(autocompleteWrapper)
    ? () => autocompleteWrapper
    : autocompleteWrapper || Styled.ContentWrapper;
  const BackdropComponent = React.isValidElement(backdrop)
    ? () => backdrop
    : backdrop || Styled.Backdrop;

  const selectedItemId =
    currentIndex != undefined && currentProductIndex == null
      ? resultItems?.[currentIndex]?.id
      : undefined;

  return (
    <React.Fragment>
      {domElements.autocompleteContainer &&
        ReactDOM.createPortal(
          <React.Fragment>
            {ReactDOM.createPortal(
              <BackdropComponent open={shouldOpen} />,
              document.body,
            )}
            <ClickAwayListener onClickAway={handleClickAway}>
              <AbsoluteContainer
                open={shouldOpen}
                topOffset={domElements.autocompleteRect?.bottom}
                onClick={handleClickAwayAutocompleteResults}
              >
                <AutocompleteWrapper
                  searchFormRect={domElements.headerRect || undefined}
                >
                  <Styled.AutocompleteResults
                    shouldShowClearItemButton
                    shouldSplitHalf={shouldShowSuggestedItems}
                    shouldShowArrows={false}
                    results={results}
                    isResultsOpen={shouldOpen}
                    withRowGap={false}
                    correctedQuery={correctionQuery?.value}
                    query={query}
                    onQueryClick={handleQueryClick}
                    onItemClick={handleItemClick}
                    onClearItem={handleClearHistoryItem}
                    searchHistory={searchHistory}
                    shouldShowHistory={shouldShowHistory}
                    shouldShowSuggestions={shouldShowSuggestions}
                    shouldShowImages={shouldShowImages}
                    shouldShowDividers={false}
                    shouldShowIcons
                    maxHistoryItemsCount={9}
                    maxMobileItemsCount={9}
                    invertQueryHighlighting
                    selectedItemId={selectedItemId}
                  />
                  {shouldShowSuggestedItems && (
                    <SuggestedItemsRenderer
                      suggestedItems={suggestedItemsToShow}
                    />
                  )}
                </AutocompleteWrapper>
              </AbsoluteContainer>
            </ClickAwayListener>
          </React.Fragment>,
          domElements.autocompleteContainer,
        )}
    </React.Fragment>
  );
};

export default AutocompleteResultsContainer;
